import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, SmartphoneIcon, CreditCardIcon, WifiIcon, GlobeIcon, HelpCircleIcon, ShieldIcon, BookOpenIcon, ArrowRightIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from '../components/Button';
type HelpCategory = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  topics: HelpTopic[];
};
type HelpTopic = {
  id: string;
  title: string;
  category: string;
};
type PopularArticle = {
  id: string;
  title: string;
  category: string;
  views: number;
};
export function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const helpCategories: HelpCategory[] = [{
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Everything you need to know to get up and running with TravelSIM',
    icon: <SmartphoneIcon className="h-6 w-6 text-blue-500" data-id="element-998" />,
    topics: [{
      id: 'what-is-esim',
      title: 'What is an eSIM?',
      category: 'getting-started'
    }, {
      id: 'compatible-devices',
      title: 'Compatible Devices',
      category: 'getting-started'
    }, {
      id: 'buying-guide',
      title: 'Buying Guide',
      category: 'getting-started'
    }, {
      id: 'activation',
      title: 'Activation Process',
      category: 'getting-started'
    }]
  }, {
    id: 'installation',
    title: 'Installation & Setup',
    description: 'Step-by-step guides for installing eSIM on different devices',
    icon: <WifiIcon className="h-6 w-6 text-green-500" data-id="element-999" />,
    topics: [{
      id: 'iphone-setup',
      title: 'iPhone Setup Guide',
      category: 'installation'
    }, {
      id: 'android-setup',
      title: 'Android Setup Guide',
      category: 'installation'
    }, {
      id: 'qr-code-scan',
      title: 'How to Scan QR Code',
      category: 'installation'
    }, {
      id: 'manual-setup',
      title: 'Manual Setup Instructions',
      category: 'installation'
    }]
  }, {
    id: 'billing',
    title: 'Billing & Payments',
    description: 'Information about payments, refunds, and billing issues',
    icon: <CreditCardIcon className="h-6 w-6 text-purple-500" data-id="element-1000" />,
    topics: [{
      id: 'payment-methods',
      title: 'Accepted Payment Methods',
      category: 'billing'
    }, {
      id: 'refund-process',
      title: 'Refund Process',
      category: 'billing'
    }, {
      id: 'billing-issues',
      title: 'Common Billing Issues',
      category: 'billing'
    }, {
      id: 'invoice-request',
      title: 'Requesting an Invoice',
      category: 'billing'
    }]
  }, {
    id: 'usage',
    title: 'Usage & Coverage',
    description: 'Information about data usage, coverage areas, and network settings',
    icon: <GlobeIcon className="h-6 w-6 text-orange-500" data-id="element-1001" />,
    topics: [{
      id: 'coverage-maps',
      title: 'Coverage Maps',
      category: 'usage'
    }, {
      id: 'data-monitoring',
      title: 'Monitoring Data Usage',
      category: 'usage'
    }, {
      id: 'network-settings',
      title: 'Network Settings',
      category: 'usage'
    }, {
      id: 'speed-tests',
      title: 'Speed Tests',
      category: 'usage'
    }]
  }, {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'Solutions for common problems and technical issues',
    icon: <HelpCircleIcon className="h-6 w-6 text-red-500" data-id="element-1002" />,
    topics: [{
      id: 'connection-issues',
      title: 'Connection Issues',
      category: 'troubleshooting'
    }, {
      id: 'activation-problems',
      title: 'Activation Problems',
      category: 'troubleshooting'
    }, {
      id: 'data-not-working',
      title: 'Data Not Working',
      category: 'troubleshooting'
    }, {
      id: 'qr-code-errors',
      title: 'QR Code Errors',
      category: 'troubleshooting'
    }]
  }, {
    id: 'account',
    title: 'Account & Security',
    description: 'Manage your account settings, security, and privacy preferences',
    icon: <ShieldIcon className="h-6 w-6 text-indigo-500" data-id="element-1003" />,
    topics: [{
      id: 'account-settings',
      title: 'Account Settings',
      category: 'account'
    }, {
      id: 'password-reset',
      title: 'Password Reset',
      category: 'account'
    }, {
      id: 'privacy-settings',
      title: 'Privacy Settings',
      category: 'account'
    }, {
      id: 'delete-account',
      title: 'Delete Account',
      category: 'account'
    }]
  }];
  const popularArticles: PopularArticle[] = [{
    id: 'esim-vs-physical',
    title: "eSIM vs Physical SIM: What's the Difference?",
    category: 'getting-started',
    views: 5423
  }, {
    id: 'iphone-setup',
    title: 'How to Set Up eSIM on iPhone 13 and Newer',
    category: 'installation',
    views: 4871
  }, {
    id: 'data-not-working',
    title: 'Troubleshooting: Data Not Working After Installation',
    category: 'troubleshooting',
    views: 3652
  }, {
    id: 'refund-process',
    title: 'How to Request a Refund for Unused eSIM',
    category: 'billing',
    views: 2910
  }];
  const filteredCategories = searchQuery ? helpCategories.filter(category => category.title.toLowerCase().includes(searchQuery.toLowerCase()) || category.topics.some(topic => topic.title.toLowerCase().includes(searchQuery.toLowerCase()))) : helpCategories;
  return <main className="py-16 w-full bg-[#F9FAFB]" data-id="element-1004">
      <div className="container mx-auto px-4" data-id="element-1005">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 md:p-12 mb-12 text-white" data-id="element-1006">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1007">
            <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1008">
              How can we help you?
            </h1>
            <p className="text-blue-100 mb-8" data-id="element-1009">
              Find answers to your questions about TravelSIM eSIMs,
              installation, usage, and more.
            </p>
            <div className="relative max-w-2xl mx-auto" data-id="element-1010">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1011">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1012" />
              </div>
              <input type="text" placeholder="Search for help articles, topics, or questions..." className="pl-12 pr-4 py-4 w-full rounded-xl border-0 text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1013" />
            </div>
          </div>
        </div>
        {/* Help Categories */}
        <div className="mb-16" data-id="element-1014">
          <h2 className="text-2xl font-bold mb-8" data-id="element-1015">Browse Help Topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1016">
            {filteredCategories.map(category => <div key={category.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:border-blue-200 transition-colors" data-id="element-1017">
                <div className="flex items-start" data-id="element-1018">
                  <div className="p-3 bg-gray-100 rounded-lg mr-4" data-id="element-1019">
                    {category.icon}
                  </div>
                  <div data-id="element-1020">
                    <h3 className="font-semibold text-lg mb-2" data-id="element-1021">
                      {category.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4" data-id="element-1022">
                      {category.description}
                    </p>
                    <ul className="space-y-2" data-id="element-1023">
                      {category.topics.map(topic => <li key={topic.id} data-id="element-1024">
                          <a href={`#${topic.id}`} className="flex items-center text-gray-700 hover:text-blue-600 text-sm" data-id="element-1025">
                            <ChevronRightIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-1026" />
                            {topic.title}
                          </a>
                        </li>)}
                    </ul>
                    <a href={`#${category.id}`} className="mt-4 inline-flex items-center text-blue-600 text-sm font-medium hover:text-blue-800" data-id="element-1027">
                      View all articles
                      <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1028" />
                    </a>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
        {/* Popular Articles */}
        <div className="mb-16" data-id="element-1029">
          <h2 className="text-2xl font-bold mb-8" data-id="element-1030">Popular Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1031">
            {popularArticles.map(article => <a key={article.id} href={`#${article.id}`} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:border-blue-200 transition-colors" data-id="element-1032">
                <div className="flex justify-between items-start" data-id="element-1033">
                  <div data-id="element-1034">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full mb-3" data-id="element-1035">
                      {article.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                    <h3 className="font-semibold text-lg mb-2" data-id="element-1036">
                      {article.title}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-1037">
                      {article.views.toLocaleString()} views
                    </p>
                  </div>
                  <BookOpenIcon className="h-5 w-5 text-gray-400" data-id="element-1038" />
                </div>
              </a>)}
          </div>
        </div>
        {/* Installation Guide Callout */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-16" data-id="element-1039">
          <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-1040">
            <div className="p-8 md:p-10" data-id="element-1041">
              <h2 className="text-2xl font-bold mb-4" data-id="element-1042">
                eSIM Installation Guide
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-1043">
                Our comprehensive installation guide walks you through the
                process of setting up your eSIM on any compatible device.
              </p>
              <ul className="space-y-3 mb-6" data-id="element-1044">
                <li className="flex items-center" data-id="element-1045">
                  <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-1046">
                    <span className="text-green-600 text-sm font-medium" data-id="element-1047">
                      1
                    </span>
                  </div>
                  <span className="text-gray-700" data-id="element-1048">
                    Step-by-step instructions
                  </span>
                </li>
                <li className="flex items-center" data-id="element-1049">
                  <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-1050">
                    <span className="text-green-600 text-sm font-medium" data-id="element-1051">
                      2
                    </span>
                  </div>
                  <span className="text-gray-700" data-id="element-1052">Visual tutorials</span>
                </li>
                <li className="flex items-center" data-id="element-1053">
                  <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-1054">
                    <span className="text-green-600 text-sm font-medium" data-id="element-1055">
                      3
                    </span>
                  </div>
                  <span className="text-gray-700" data-id="element-1056">Troubleshooting tips</span>
                </li>
              </ul>
              <Link to="/installation-guide" data-id="element-1057">
                <Button variant="primary" data-id="element-1058">View Installation Guide</Button>
              </Link>
            </div>
            <div className="bg-gray-100 flex items-center justify-center p-8" data-id="element-1059">
              <img src="https://images.unsplash.com/photo-1605236453806-6ff36851218e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=764&q=80" alt="eSIM Installation Guide" className="max-h-64 rounded-lg shadow-md" data-id="element-1060" />
            </div>
          </div>
        </div>
        {/* Contact Support */}
        <div className="bg-gray-100 rounded-xl p-8 text-center" data-id="element-1061">
          <h2 className="text-2xl font-bold mb-4" data-id="element-1062">
            Can't find what you're looking for?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-1063">
            Our support team is available 24/7 to help you with any questions or
            issues you may have.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-4" data-id="element-1064">
            <Link to="/contact" data-id="element-1065">
              <Button variant="primary" data-id="element-1066">Contact Support</Button>
            </Link>
            <Button variant="outline" data-id="element-1067">Live Chat</Button>
          </div>
        </div>
      </div>
    </main>;
}